// src/api/axiosInstance.ts
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';

/**
 * The `error` values RestAuthenticationEntryPoint puts in its JSON body, and the reason this file
 * exports a predicate instead of every instance testing `status === 401`.
 *
 * The server no longer answers a dead token with 401. It answers 403, because the mobile builds
 * already installed on phones log out on 403 and ignore 401 outright, and there is no OTA update —
 * a 401 leaves such a phone holding a token it never clears. The revert to 401 is planned but has
 * to wait for a release to reach users (see RestAuthenticationEntryPoint.AUTH_FAILURE_STATUS).
 *
 * So on the wire 403 now means two unrelated things: "your session is over" and "you are signed in
 * and still may not". The status cannot separate them; the body can. Only the auth entry point
 * writes one of these codes into `error` — an authorisation refusal carries Spring Boot's generic
 * `{"status":403,"error":"Forbidden"}` from the /error dispatch, an admin controller's own refusal
 * carries a Slovak sentence, and a rejected CORS preflight carries no JSON at all. None of them can
 * collide with the list below, because the entry point is the only writer of it.
 */
const AUTH_ERROR_CODES = ['token_expired', 'invalid_token', 'not_authenticated'];

/** Reads `error` out of whatever shape the body arrived in. Never throws: an unreadable body is
 *  simply not proof of a dead session, and guessing wrong here signs someone out for nothing. */
const carriesAuthErrorCode = (data: any): boolean => {
    if (typeof data === 'string') {
        // responseType 'text', or a JSON body axios did not parse because of the content type.
        try {
            return carriesAuthErrorCode(JSON.parse(data));
        } catch {
            return false;
        }
    }
    if (data && typeof data === 'object') {
        const code = data.error;
        return typeof code === 'string' && AUTH_ERROR_CODES.indexOf(code) !== -1;
    }
    return false;
};

/**
 * "This session cannot continue and no retry will help" — the one condition every instance in this
 * folder has to react to by clearing the token.
 *
 * 401 qualifies on the status alone: the API returns it only for a missing, expired or invalid
 * token, and it is what this check will see again once AUTH_FAILURE_STATUS goes back to
 * UNAUTHORIZED. 403 qualifies only when the body names one of the auth error codes, which is what
 * keeps "not an admin" from being read as "signed out". Both arms are live at once on purpose:
 * flipping the server constant back needs no change here.
 *
 * Async because of one caller shape — the invoicing CSV/ZIP export and the invoice PDF are fetched
 * with `responseType: 'blob'`, so their error body is a Blob and reading it is a promise. Reading a
 * Blob does not consume it, so the screen's own error handling still sees the same response.
 * ArrayBuffer bodies are deliberately not decoded: the only instance that requests them is `api`
 * below, whose rule short-circuits before the body is ever consulted.
 */
export const isSessionOver = async (error: any): Promise<boolean> => {
    const status = error?.response?.status;
    if (status === 401) {
        return true;
    }
    if (status !== 403) {
        return false;
    }
    const data = error.response.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            return carriesAuthErrorCode(await data.text());
        } catch {
            return false;
        }
    }
    return carriesAuthErrorCode(data);
};

// Create an Axios instance
const api = axios.create({
    baseURL: config.API_URL,
});

// Add a request interceptor
api.interceptors.request.use(
    (requestConfig) => {
        const state = store.getState(); // Access the Redux state directly
        const token = state.auth.accessToken;

        // Exclude login and register endpoints from having the Authorization header
        if (token && !requestConfig.url?.includes('/login') && !requestConfig.url?.includes('/register')) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle errors like token expiration
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        // This instance serves the whole site, where no screen has a use for a 403 of its own: an
        // expired token used to come back as a 500, which reads as "server problem, try again", so
        // the dead token was never cleared and every screen kept retrying with it. Both 401 and 403
        // mean this session cannot continue, so both clear it.
        //
        // The plain-403 arm is deliberately broader than isSessionOver(): it also catches an
        // authorisation refusal, which strictly speaking should show a message rather than sign the
        // user out. That is the behaviour the deployed web bundle and every installed mobile build
        // already have, and narrowing it here would be a silent change to every screen at once.
        // When AUTH_FAILURE_STATUS goes back to UNAUTHORIZED this arm is what should be deleted;
        // isSessionOver() alone is then the correct rule, and it already covers 401 today.
        const isPlainForbidden = status === 403;
        const isAuthFailure = isPlainForbidden || await isSessionOver(error);
        // ...except on the auth endpoints themselves, where 401 means "wrong e-mail or password"
        // and the login form shows its own message. Logging out there would be answering a failed
        // sign-in by signing the user out.
        const isAuthEndpoint = error.config?.url?.includes('/v1/auth');

        if (isAuthFailure && !isAuthEndpoint) {
            store.dispatch(logout()); // Log the user out
        }
        return Promise.reject(error);
    }
);

export default api;
